import {
  CommonActions,
  NavigationProp,
  ParamListBase,
} from '@react-navigation/native';
import {
  FINANCE_HUB_ROUTES,
  FINANCE_HUB_TAB_ROUTES,
} from '@/navigators/routes';
import { FinanceHubStackParams } from '@/navigators/types';

export const FINANCE_HUB_PATH = `/finance-hub`;

export const autopayTabScreens = {
  [FINANCE_HUB_ROUTES.Autopay]: {
    path: `${FINANCE_HUB_PATH}/autopay`,
  },
  [FINANCE_HUB_ROUTES.AutopayRunPreview]: {
    path: `${FINANCE_HUB_PATH}/autopay/:autopaySettingId/preview`,
  },
};

export const directTabScreens = {
  [FINANCE_HUB_ROUTES.Home]: {
    path: FINANCE_HUB_PATH,
  },
};

export const customersTabScreens = {
  [FINANCE_HUB_ROUTES.Customers]: {
    path: `${FINANCE_HUB_PATH}/customers`,
  },
};

export const invoicesTabScreens = {
  [FINANCE_HUB_ROUTES.Invoices]: {
    path: `${FINANCE_HUB_PATH}/invoices`,
  },
  [FINANCE_HUB_ROUTES.NotFound]: {
    path: `${FINANCE_HUB_PATH}/not-found`,
  },
};

/**
 * Builds a `screenName -> parentTab` entry list from a tab-screen grouping.
 */
const screensInTab = (
  screens: Record<string, unknown>,
  parentTab: FINANCE_HUB_TAB_ROUTES | null,
): [string, FINANCE_HUB_TAB_ROUTES | null][] =>
  Object.keys(screens).map((screen) => [screen, parentTab]);

/**
 * Maps each Finance Hub stack screen to the tab that contains it. Used by the
 * sidebar nav to build nested-navigator payloads for lazy-loaded tabs.
 */
export const ROUTE_PARENT_TAB: Record<
  keyof FinanceHubStackParams,
  FINANCE_HUB_TAB_ROUTES | null
> = Object.fromEntries([
  ...screensInTab(autopayTabScreens, FINANCE_HUB_TAB_ROUTES.Autopay),
  ...screensInTab(customersTabScreens, FINANCE_HUB_TAB_ROUTES.Customers),
  ...screensInTab(invoicesTabScreens, FINANCE_HUB_TAB_ROUTES.Invoices),
  ...screensInTab(directTabScreens, null),
]) as Record<keyof FinanceHubStackParams, FINANCE_HUB_TAB_ROUTES | null>;

interface NavigateDispatcher {
  dispatch: NavigationProp<ParamListBase>['dispatch'];
}

/**
 * Navigates to a Finance Hub route, wrapping the action in the nested-navigator
 * payload when the target lives inside a tab's stack
 */
export function navigateToFinanceHubRoute<
  R extends keyof FinanceHubStackParams,
>(
  navigation: NavigateDispatcher,
  route: R,
  params?: FinanceHubStackParams[R],
): void {
  const parentTab = ROUTE_PARENT_TAB[route];
  if (parentTab) {
    navigation.dispatch(
      CommonActions.navigate({
        name: parentTab as string,
        params: { params, screen: route as string },
      }),
    );
  } else {
    navigation.dispatch(
      CommonActions.navigate({ name: route as string, params }),
    );
  }
}
